import React, { useEffect, useMemo, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import {
  cancelNoBillLedgers,
  closeNoBillLedgers,
  commitNoBillLedgers,
  fetchNoBillState,
  stageNoBillLedgers,
  type NoBillStateDTO,
} from "../../data/noBillApi";
import type { LedgerPushDTO } from "../../data/ledgerImportApi";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { CheckIcon, CircleIcon, WarningIcon, XIcon } from "../../components/icons";
import { cn } from "../../components/cn";

/**
 * The No Bill parent ledger and the sub-ledgers under it.
 *
 * Expenses that arrive without a bill have to be findable again — to chase the
 * document, to keep them out of input credit, to answer for them at year end. So
 * they get their own account rather than disappearing into the ordinary expense
 * trail.
 *
 * NAMING IS FREE; THE BUTTON IS NOT. Everything above the push button is local:
 * the categories are staged in our own tables and can be changed as often as anybody
 * likes. One button writes to the client's Zoho Books, and nothing it does can be
 * undone — Zoho will not delete an account with an entry against it.
 *
 * WHAT IS ALREADY THERE IS SHOWN, NEVER RE-CREATED. A CA coming back to this step
 * must not end up with a second `No Bill - Festival Expenses`; Zoho has no
 * idempotency key and would not tell the two apart. The chart of accounts is what
 * we believe, not a list of our own.
 */

/**
 * How many batches one press of the button may run.
 *
 * A no-bill set is a dozen rows, so one batch does it — but the loop is the same
 * shape as the sheet import's, and the cap is here for the same reason: a loop
 * driven by a server field must not hammer the server if that field ever sticks.
 */
const MAX_BATCHES = 10;

/**
 * What every sub-ledger is called before its category.
 *
 * Kept in step with `NoBillLedgerService::PREFIX` by hand, which is the cost of
 * the server owning the naming. If it ever changes there, this has to move with it
 * or a typed name carrying the old prefix stops being recognised as one.
 */
const PREFIX = "No Bill - ";

export function NoBillPanel({
  clientId,
  clientName,
  organisationName,
  onPushed,
}: {
  clientId: ClientId;
  clientName: string;
  /** Named on the push button, because "whose books" is the question it answers. */
  organisationName?: string | null;
  /** A push created accounts, so the chart of accounts above is now out of date. */
  onPushed?: () => void;
}) {
  const [state, setState] = useState<NoBillStateDTO | null>(null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [push, setPush] = useState<LedgerPushDTO | null>(null);
  // A push spans several requests. If the CA clicks to another sub-step
  // part-way, the remaining batches must not keep firing into a dead screen.
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;

    fetchNoBillState(clientId)
      .then((result) => {
        if (!liveRef.current) return;
        setState(result);
        // Staged rows are the answer to "what did I pick", so a reload comes
        // back with the categories the CA left rather than an empty list beside a
        // staged set nobody can see the origin of.
        setPicked(new Set(result.rows.filter((row) => !row.is_parent).map((row) => row.label)));
      })
      .catch((cause: unknown) => {
        if (liveRef.current) {
          setError(cause instanceof Error ? cause.message : "Could not read the No Bill ledgers.");
        }
      });

    return () => {
      liveRef.current = false;
    };
  }, [clientId]);

  /** Everything the client already has, by label, so a tick can say "already there". */
  const existingLabels = useMemo(() => {
    const out = new Set<string>();
    for (const child of state?.existing ?? []) out.add(fold(child.label));

    return out;
  }, [state]);

  function apply(result: NoBillStateDTO) {
    setState(result);
    setError(null);
  }

  function toggle(label: string) {
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(label)) next.delete(label);
      else next.add(label);

      return next;
    });
  }

  /**
   * Add what was typed, normalised THE SAME WAY THE SERVER WILL.
   *
   * These three rules mirror `NoBillLedgerService::cleanLabels`, and the mirroring
   * is not tidiness — the panel is a preview of what the server is going to do, and
   * any rule it does not apply is a way for the preview to disagree with reality.
   *
   * It did disagree, and the failure was worse than a wrong label. Typing
   * `No Bill - DSC Charges` left the browser holding that whole string while the
   * server stripped the prefix, and typing a case variant of a name already on the
   * list left the browser with two where the server had one. Either way the staged
   * set no longer matched what was on screen, so the stale guard fired and the
   * panel offered "Work it out again" — which re-staged the same rows and left the
   * same divergence. A dead end with no way forward but Start over.
   */
  function addTyped() {
    // Collapse the whitespace first: "Travel  Expense" and "Travel Expense" are
    // one category to a person and two account names to Zoho.
    let label = typed.trim().replace(/\s+/g, " ");

    // Somebody typing the prefix as well would otherwise get
    // "No Bill - No Bill - DSC Charges". Stripped rather than refused: it is
    // obvious what they meant.
    if (fold(label).startsWith(fold(PREFIX))) {
      label = label.slice(PREFIX.length).trim();
    }

    if (label === "") return;

    setPicked((previous) => {
      // Case-insensitively, because that is how Zoho compares account names and
      // how the server dedupes. `Staff Welfare` and `staff welfare` are one
      // category, not two.
      for (const existing of Array.from(previous)) {
        if (fold(existing) === fold(label)) return previous;
      }

      return new Set(previous).add(label);
    });
    setTyped("");
  }

  /** Stage what was named: work out what would be new, and write that down. */
  function stage() {
    setBusy(true);
    setError(null);
    setPush(null);

    stageNoBillLedgers(clientId, Array.from(picked))
      .then(apply)
      .catch((cause: unknown) => {
        // Every message from here is written for a CA — "the parent is marked
        // inactive in Zoho" — so it goes on screen as it is.
        setError(cause instanceof Error ? cause.message : "Could not work out what to create.");
      })
      .finally(() => setBusy(false));
  }

  function discard() {
    setBusy(true);

    cancelNoBillLedgers(clientId)
      .then((result) => {
        apply(result);
        setPush(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not discard the staged ledgers.");
      })
      .finally(() => setBusy(false));
  }

  function close() {
    setBusy(true);

    closeNoBillLedgers(clientId)
      .then((result) => {
        apply(result);
        setPush(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not close the push.");
      })
      .finally(() => setBusy(false));
  }

  /**
   * Run the push to completion, or to the first reason to stop.
   *
   * Sequential on purpose: the parent created by one batch is what the
   * next batch hangs children off, so overlapping two would race on exactly that.
   */
  async function runPush() {
    setPushing(true);
    setError(null);

    try {
      for (let batch = 0; batch < MAX_BATCHES; batch++) {
        const result = await commitNoBillLedgers(clientId);
        if (!liveRef.current) return;

        setState(result);
        setPush(result.push ?? null);

        // `halted` is a rate limit or a refused credential — not "go slower".
        // The next call would hit the same wall.
        if (!result.push || result.push.status === "done" || result.push.halted) {
          if (result.push?.status === "done") onPushed?.();
          break;
        }
      }
    } catch (cause: unknown) {
      if (!liveRef.current) return;

      setError(cause instanceof Error ? cause.message : "The push stopped.");

      // Whatever went wrong, which rows made it is a fact on the server rather
      // than in whatever we last rendered.
      await fetchNoBillState(clientId)
        .then((fresh) => {
          if (liveRef.current) setState(fresh);
        })
        .catch(() => undefined);
    } finally {
      if (liveRef.current) setPushing(false);
    }
  }

  if (state === null) {
    return (
      <div className="flex flex-col gap-3 rounded-md border p-4">
        <span className="text-xs font-medium text-muted-foreground">No Bill ledgers</span>
        {error !== null ? (
          <Callout variant="warning">{error}</Callout>
        ) : (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
      </div>
    );
  }

  if (!state.chart_read) {
    return (
      <div className="flex flex-col gap-3 rounded-md border p-4">
        <span className="text-xs font-medium text-muted-foreground">No Bill ledgers</span>
        <Callout variant="warning">
          We have not read {clientName}'s chart of accounts yet, so there is nothing to add these to.
        </Callout>
      </div>
    );
  }

  const staged = state.import;
  const pushed = staged !== null && staged.status === "done";
  const rows = state.rows;
  /**
   * Whose books, said so it reads as books.
   *
   * The organisation name alone made the button read "Create 2 in Local Test",
   * which sounds like an environment or a place rather than a customer's
   * accounting system — and naming the system is the entire reason the
   * organisation is on the button at all.
   */
  const books = `${organisationName ?? clientName}'s Zoho Books`;
  const toPush = staged === null ? 0 : staged.new_rows;
  const retryable = staged !== null && staged.status === "committing";
  /**
   * What was typed has moved on from the list that was worked out.
   *
   * Without this the button keeps naming the old count — "Create 1" beside three
   * three named categories — and creating one of them looks like the other two
   * silently failed. A stale count on the one button that writes to a customer's
   * books is not a cosmetic problem, so the button becomes Review again rather
   * than pushing something nobody asked for.
   *
   * Not while a push is under way: mid-push the staged list is a record of what
   * is being applied, and the list cannot change what that is any more.
   */
  const stagedLabels = new Set(rows.filter((row) => !row.is_parent).map((row) => fold(row.label)));
  const wantedNew = Array.from(picked)
    .map(fold)
    .filter((label) => !existingLabels.has(label));
  const stale =
    staged !== null &&
    staged.status === "validated" &&
    (wantedNew.length !== stagedLabels.size || wantedNew.some((label) => !stagedLabels.has(label)));
  // Everything the CA has named, minus anything already in the books — those are
  // in the tree as present rather than repeated as work.
  const added = Array.from(picked).filter((label) => !existingLabels.has(fold(label)));

  /**
   * The tree: the parent ledger, and every sub-ledger that is there or going to be.
   *
   * Built from three sources on purpose, because each knows something the others
   * do not. The chart says what exists. `picked` says what the CA has named. The
   * staged rows say what Zoho did with each one, and only exist after Review.
   *
   * A staged row is matched to a child by NAME rather than by id, because a child
   * that has not been created has no id to match on — the name is the only handle
   * that spans both sides.
   */
  const stagedByLabel = new Map(rows.map((row) => [fold(row.label), row]));
  // By its own flag, not by name: a staged row's `label` has the `No Bill - `
  // prefix stripped, so the parent's is "Expense" and looking it up by its
  // full name silently misses — which read as the parent never having been created.
  const parentStaged = rows.find((row) => row.is_parent);
  const parentStatus: TreeStatus =
    parentStaged?.status === "created" ? "created" : state.parent.exists ? "in-books" : "to-add";

  const children = [
    ...state.existing.map((child) => ({
      key: child.account_id,
      label: child.label,
      name: child.account_name,
      // `created` beats `in-books` while the push's outcome is still on screen.
      // The push re-reads the chart as its last act, so by the time this renders
      // everything it made is ALSO an existing child — and reporting it that way
      // would leave a CA who just pressed the button looking at a list that says
      // nothing about what their press did.
      status: (stagedByLabel.get(fold(child.label))?.status === "created"
        ? "created"
        : "in-books") as TreeStatus,
      // Loose in the chart rather than under the parent. Worth saying: its name
      // is taken either way, so it is never re-created.
      note: child.under_parent ? null : "not under the parent",
      error: null as string | null,
      removable: false,
    })),
    ...added.map((label) => {
      const row = stagedByLabel.get(fold(label));
      const refused = row !== undefined && (row.status === "failed" || row.issues.length > 0);

      return {
        key: label,
        label,
        name: `No Bill - ${label}`,
        status: (row?.status === "created" ? "created" : refused ? "refused" : "to-add") as TreeStatus,
        note: null,
        error: refused ? (row?.error ?? row?.issues.map((issue) => issue.message).join(" ") ?? null) : null,
        // A row Zoho has already created cannot be taken off the list by
        // unticking it — it is in the books.
        removable: row?.status !== "created",
      };
    }),
  ];
  /*
   * NO PICKER OVER THEIR CHART HERE, and that is the line between this option and
   * the mapping one.
   *
   * This option creates new ledgers. The other points at ledgers they already
   * have. A list of their accounts offered inside this one borrowed the other's
   * mechanism: nothing was ever linked — a name was copied and a new account
   * created beside the original — but the affordance said otherwise, and read as
   * though ticking `Credit Card Charges` did something to `Credit Card Charges`.
   *
   * So the CA types. If what they type happens to match a ledger they have, that
   * is fine and costs nothing: `No Bill - Travel Expense` is a different account
   * name from `Travel Expense`, so Zoho takes it, and the only real collision —
   * a `No Bill - ` twin that already exists — is caught by the validator and shown
   * in the tree as already there.
   */

  /**
   * A list has been worked out against Zoho and nothing has been sent yet.
   *
   * The one state where the panel is making a claim about what a button will do to
   * a customer's books, rather than showing what somebody has typed.
   */
  const previewing = staged !== null && !stale && !pushed && toPush > 0;

  // Shared by the preview and the editing state, so the picture of the parent and
  // its sub-ledgers cannot drift between them.
  const tree = (
    <ul className="flex flex-col gap-1 text-sm">
      <TreeRow label={state.parent.name} status={parentStatus} depth={0} />
      {children.map((child) => (
        <TreeRow
          key={child.key}
          label={child.name}
          note={child.note}
          error={child.error}
          status={child.status}
          depth={1}
          onRemove={
            child.removable && !busy && !pushing && !previewing ? () => toggle(child.label) : undefined
          }
        />
      ))}
    </ul>
  );

  return (
    <div className="flex flex-col gap-4 rounded-md border p-4">
      {/*
        A label and then the tree. No sentence describing the shape, because the
        tree IS the shape — `No Bill - Expense` with names indented under it says
        "one ledger per kind of spend" better than that sentence did, and the
        decision card above has already given the reason.
      */}
      <span className="text-xs font-medium text-muted-foreground">No Bill ledgers</span>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      {state.parent.inactive && (
        <Callout variant="warning">
          {state.parent.name} is marked inactive in Zoho. Nothing can be added under it until somebody
          makes it active there.
        </Callout>
      )}

      {/*
        THE TREE IS THE WHOLE ANSWER, and it replaced three blocks that each told
        part of it: a sentence describing the shape, chips for what had been named,
        and a staged list repeating them with outcomes. One picture of the parent
        and what sits under it says all three at once, and says the thing prose
        could not — that these are children of that parent.

        It also retires a caption. "Nothing added. We will add No Bill - Expense on
        its own" was prose for a tree with a root and no branches, which is what
        the reader is now looking at.
      */}
      {/*
        REVIEW IS THE PREVIEW, and this is where that shows.

        It used to be a third click: Review staged the rows, and the summary of
        what would be written only appeared after pressing Create ledgers — so the
        one screen that named the organisation and said the writes could not be
        undone was hidden behind a button most people would press without reading.
        Reviewing is the moment somebody is asking "what exactly will this do", so
        that is the moment it answers.

        The typing field goes away here on purpose. This state is a statement about
        a list that has been worked out against Zoho; leaving the field open would
        invite edits that silently make the statement wrong — which is the very
        thing the `stale` guard exists to catch.
      */}
      {previewing ? (
        <div className="flex flex-col gap-3 rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
          <p className="text-sm">
            This adds {toPush} {toPush === 1 ? "ledger" : "ledgers"} to{" "}
            <span className="font-medium">{books}</span>.
          </p>

          {tree}
        </div>
      ) : (
        <>
          {tree}

          {/*
            TYPED, NOT TICKED, and that is the whole difference between this and
            the mapping panel. There the CA is choosing accounts, so a list of every
            expense ledger is exactly right. Here they are NAMING new ones, and a
            wall of thirty-six checkboxes is the wrong instrument twice over: most
            of a chart — Depreciation, Purchase Discounts, Exchange Gain or Loss —
            will never see spend without a bill, and a list cannot hold a category
            the client needs that is not a ledger yet.
          */}
          {!pushed && (
            <div className="flex flex-col gap-2">
              {/* Optional, and meant literally: none is a real answer, and it
                  produces the single-bucket setup the tree above shows as a root
                  with no branches. */}
              <span className="text-xs font-medium text-muted-foreground">Sub-ledgers (optional)</span>

              <div className="flex items-center gap-2">
                <input
                  value={typed}
                  onChange={(event) => setTyped(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      addTyped();
                    }
                    if (event.key === "Escape") setTyped("");
                  }}
                  placeholder="Travel, staff welfare, festival expenses…"
                  disabled={busy || pushing}
                  className="h-9 flex-1 rounded-md border bg-background px-3 text-sm"
                />
                <CoButton
                  variant="outline"
                  onClick={addTyped}
                  disabled={typed.trim() === "" || busy || pushing}
                >
                  Add
                </CoButton>
              </div>
            </div>
          )}
        </>
      )}

      {push !== null && push.halted && push.halted_reason !== null && (
        <Callout variant="warning">{push.halted_reason}</Callout>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t pt-3">
        {pushed ? (
          <>
            <CoButton onClick={close} disabled={busy}>
              Close
            </CoButton>
            {staged !== null && staged.failed_rows > 0 && (
              <span className="text-xs text-muted-foreground">
                Fix what Zoho refused, then stage the categories again.
              </span>
            )}
          </>
        ) : staged === null || stale ? (
          <>
            <CoButton onClick={stage} disabled={busy || pushing || state.parent.inactive}>
              {busy ? "Working…" : stale ? "Work it out again" : "Review what would be created"}
            </CoButton>
            {/* Only when the tree above cannot say it. What is in the list is
                visible; that the list has moved on from what was worked out
                against Zoho is not. */}
            {stale && (
              <span className="text-xs text-muted-foreground">The list changed. Work it out again.</span>
            )}
          </>
        ) : (
          <>
            {/* Straight to Zoho, because the preview above IS the confirmation —
                it names the organisation, lists what will be created, and says the
                writes cannot be undone. A second dialog restating that would be
                one people learn to click through. */}
            <CoButton onClick={() => void runPush()} disabled={pushing || busy || toPush === 0}>
              {pushing
                ? "Creating…"
                : retryable
                  ? `Continue - ${push?.remaining ?? "some"} left`
                  : "Create ledgers"}
            </CoButton>
            <CoButton variant="outline" onClick={discard} disabled={busy || pushing}>
              Start over
            </CoButton>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Where one ledger stands.
 *
 * `in-books` and `created` are both "it exists", kept apart because the second is
 * news — a CA who has just pressed the button wants to see what their press did,
 * not a list that looks the same as before it.
 */
type TreeStatus = "in-books" | "created" | "to-add" | "refused";

/**
 * Words only where the icon is not enough, which is rarely.
 *
 * A green tick already says a ledger is in their books and an empty circle already
 * says it is not, so labelling either says it twice — and it is every row, which
 * makes a column of identical micro-text nobody reads. In the preview the headline
 * has already said what the whole list is for.
 *
 * The two that keep their words are the two a reader cannot infer: `created` is
 * news about what a press just did, and `refused` is a problem.
 */
const STATUS_LABEL: Record<TreeStatus, string | null> = {
  "in-books": null,
  created: "created",
  "to-add": null,
  refused: "refused",
};

/**
 * One line of the tree.
 *
 * Indent plus a rule down the left rather than box-drawing characters: it survives
 * a name long enough to wrap, which `└─` does not.
 */
function TreeRow({
  label,
  note,
  error,
  status,
  depth,
  onRemove,
}: {
  label: string;
  note?: string | null;
  error?: string | null;
  status: TreeStatus;
  depth: number;
  /** Absent when the row cannot be taken off the list — it is already in Zoho. */
  onRemove?: () => void;
}) {
  return (
    <li className={cn("flex flex-col gap-0.5", depth > 0 && "ml-2 border-l pl-3")}>
      <span className="flex items-center gap-1.5">
        {status === "in-books" || status === "created" ? (
          <CheckIcon className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        ) : status === "refused" ? (
          <WarningIcon className="size-3.5 shrink-0 text-amber-600 dark:text-amber-400" />
        ) : (
          // Waiting, not wrong. A cross here read as "this one failed" or as the
          // button to take it off the list, and it is neither.
          <CircleIcon className="size-3.5 shrink-0 text-muted-foreground/40" />
        )}

        <span className={cn("truncate", status === "to-add" && "text-muted-foreground")}>{label}</span>

        {STATUS_LABEL[status] !== null && (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground/60">
            {STATUS_LABEL[status]}
          </span>
        )}

        {note !== null && note !== undefined && (
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground/80">{note}</span>
        )}

        {onRemove !== undefined && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove ${label}`}
            className="ml-auto shrink-0 text-muted-foreground hover:text-foreground"
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </span>

      {/* The reason, not a status word. "Zoho keeps ledger names unique" is
          something a CA can act on; "refused" is not. */}
      {error !== null && error !== undefined && error !== "" && (
        <span className="pl-5 text-xs text-amber-700 dark:text-amber-400">{error}</span>
      )}
    </li>
  );
}

/** Names compare the way Zoho compares them: case and edge whitespace do not count. */
function fold(value: string): string {
  return value.trim().toLowerCase();
}

